import { compact, difference, differenceBy, isEqual, keyBy } from "lodash";

import Role from "constants/role";
import { TICKET_PRIORITY_LABEL_MAP } from "constants/tickets/priorities";
import { escapeString } from "helpers/string";
import {
  Author,
  EventType,
  IAssignmentEvent,
  IEvent,
  IRequester,
  TicketSiloFormattedValues,
  TicketStatusFormattedValues,
} from "interfaces/entities/ticket";
import { AutomationsRoutes } from "routes/automations/constants";
import { TicketEventRating } from "routes/tickets/ticket/ticket-events";
import { IAgent } from "store/entities/agents";
import { ILicenseSettings } from "store/entities/license";
import { IRule } from "store/entities/rules/interfaces";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { TicketMessage } from "store/views/tickets/interfaces";
import { plural } from "utils/string";

import { isInIframe } from "./app";
import { getSpamReasonMessage, messageIsEmpty, parseEventMessageToHtml, parseRichTextFormatToHtml } from "./tickets";

function getAuthorName(
  agents: IAgent[],
  authorId: string,
  authorNameFallback: string,
  authorEmailFallback: string
): string | null {
  const agent = agents.find((agentItem: IAgent) => agentItem.id === authorId);

  const agentName = agent ? escapeString(agent.name) : escapeString(authorNameFallback || authorEmailFallback);
  const agentEmail = agent ? escapeString(agent.email) : escapeString(authorEmailFallback);

  return `<strong title="${agentEmail}">${agentName}</strong>`;
}

function getRuleName(rulesMap: Record<string, IRule>, currentAgent: IAgent, id?: string): string {
  if (!id) {
    return "<strong>(deleted rule)</strong>";
  }
  const ruleName = rulesMap[id]?.name || "(deleted rule)";
  const wrapLink = currentAgent.permission !== Role.Viewer && rulesMap[id]?.name;
  const isIframe = isInIframe();

  return wrapLink
    ? `<a class="rule-link" href="${AutomationsRoutes.Rules}/#${id}"${
        isIframe ? ' target="_blank" rel="nofollow noreferrer noopener"' : ""
      }>${escapeString(ruleName)}</a>`
    : ruleName;
}

function getTeamName(teams: ITeam[], teamId: string, teamNameFallback: string): string | null {
  const team = teams.find((teamItem: ITeam) => teamItem.id === teamId);
  return team ? team.name : teamNameFallback;
}

function getAgentName(agents: IAgent[], agentId?: string, agentNameFallback: string = "agent"): string | null {
  if (!agentId) {
    return `<strong>${escapeString(agentNameFallback)}</strong>`;
  }
  const systemUser = {
    id: "00000000-0000-4000-8000-000000000000",
    name: "HelpDesk",
    title: "HelpDesk",
  };
  if (agentId === systemUser.id) {
    return `<strong title=${systemUser.title}>${systemUser.name}</strong>`;
  }
  const agent = agents.find((agentItem: IAgent) => agentItem.id === agentId);
  return agent
    ? `<strong title="${escapeString(agent.email)}">${escapeString(agent.name)}</strong>`
    : `<strong>${escapeString(agentNameFallback)}</strong>`;
}

export function getTicketMessageComponentsFromEvent(
  event: IEvent,
  requester: IRequester,
  agents: IAgent[],
  teams: ITeam[],
  rules: IRule[],
  tags: ITag[],
  currentAgent: IAgent,
  settings: Pick<ILicenseSettings, "showAgentTagChangeEvents">
): TicketMessage[] {
  let requesterName = "";
  if (requester) {
    requesterName = requester.name || requester.email;
  }

  const tagsMap = keyBy(tags, t => t.id);
  const rulesMap = keyBy(rules, r => r.id);

  switch (event.type) {
    case EventType.FollowersChanged: {
      const events = [];
      if (!event.followers) {
        return [];
      }

      const deletedFollowers = differenceBy(event.followers.old, event.followers.new, ({ ID }) => ID);
      const addedFollowers = differenceBy(event.followers.new, event.followers.old, ({ ID }) => ID);

      if (
        addedFollowers.length === 1 &&
        deletedFollowers.length === 0 &&
        event.author?.type === Author.Agent &&
        addedFollowers[0].ID === event.author.id
      ) {
        return [`${getAgentName(agents, event.author.id, event.author.name)} started to follow this ticket.`];
      }

      if (
        addedFollowers.length === 0 &&
        deletedFollowers.length === 1 &&
        event.author?.type === Author.Agent &&
        deletedFollowers[0].ID === event.author.id
      ) {
        return [`${getAgentName(agents, event.author.id, event.author.name)} unfollowed this ticket.`];
      }

      let restMessage = "";
      if (addedFollowers.length > 0) {
        restMessage += `made ${addedFollowers.map(({ name }) => `<strong>${name}</strong>`).join(", ")} follow`;
      }

      if (addedFollowers.length > 0 && deletedFollowers.length > 0) {
        restMessage += " and ";
      }

      if (deletedFollowers.length > 0) {
        restMessage += `made ${deletedFollowers.map(({ name }) => `<strong>${name}</strong>`).join(", ")} unfollow`;
      }

      if (restMessage === "") {
        return [];
      }

      restMessage += " this ticket.";

      if (event.author?.type === "rule") {
        events.push(`Rule ${getRuleName(rulesMap, currentAgent, event.author.id)} ${restMessage}`);
      } else {
        events.push(`${getAgentName(agents, event.author?.id, event.author?.name)} ${restMessage}`);
      }

      return events;
    }
    case EventType.Message: {
      const events = [];
      if (event.author?.type === "rule") {
        events.push(
          `Rule ${getRuleName(rulesMap, currentAgent, event.author.id)} sent ${
            event.isPrivate ? "private note" : "message"
          }${event.isPrivate ? "." : ` to <strong>${escapeString(requesterName)}</strong>.`}`
        );
      }

      if (event.messageRichText) {
        events.push(<>{parseRichTextFormatToHtml(event.messageRichText)}</>);
      } else if (event.messageHtml && !messageIsEmpty(event.messageHtml)) {
        events.push(<>{event.messageHtml}</>);
      } else {
        events.push(<>{parseEventMessageToHtml(event.messageText)}</>);
      }

      return events;
    }

    case EventType.Summary: {
      const events = [];
      if (event.author?.type === Author.Agent) {
        events.push(
          // @ts-expect-error TS(2304): Cannot find name 'getAuthorName'.
          `${getAuthorName(agents, event.author.id, event.author.name, event.author.email)} summarized the ticket.`
        );
      }

      if (event.summary) {
        // @ts-expect-error TS(2304): Cannot find name 'events'.
        events.push(<>{parseRichTextFormatToHtml(event.summary.richText)}</>);
        // @ts-expect-error TS18048
      } else if (event.summary.html && !messageIsEmpty(event.summary.html)) {
        // @ts-expect-error TS(2304): Cannot find name 'events'.
        events.push(<>{event.summary.html}</>);
      } else {
        // @ts-expect-error TS(2304): Cannot find name 'events'.
        events.push(<>{parseEventMessageToHtml(event.summary.text)}</>);
      }

      return events;
    }

    case EventType.Assignment: {
      if (!event.assignment) {
        return [];
      }
      const { old: oldAssignment, new: newAssignment } = event.assignment;
      const messages = [];

      const getTeamMessage = (assignmentEvent: IAssignmentEvent): string => {
        if (!assignmentEvent.team) {
          return "<em>(deleted group)</em>";
        }
        const teamName = getTeamName(teams, assignmentEvent.team.ID, assignmentEvent.team.name);
        return teamName ? `<strong>${escapeString(teamName)}</strong>` : "<em>(deleted group)</em>";
      };

      const getNewTeamMessage = (): string => {
        return getTeamMessage(newAssignment);
      };

      const getOldTeamMessage = (): string => {
        return getTeamMessage(oldAssignment);
      };

      const getAgentMessage = (): {
        newAgentName: string | null;
        oldAgentName: string | null;
      } => {
        const newAgentName = newAssignment.agent
          ? getAgentName(agents, newAssignment.agent.ID, newAssignment.agent.name)
          : null;
        const oldAgentName = oldAssignment.agent
          ? getAgentName(agents, oldAssignment.agent.ID, oldAssignment.agent.name)
          : null;
        return { newAgentName, oldAgentName };
      };

      const authorMessage = (action: string, subject: string): string => {
        if (event.author) {
          if (event.author.type === Author.Rule) {
            return `Rule ${getRuleName(rulesMap, currentAgent, event.author.id)} ${action} ${subject}.`;
          }
          return `${getAgentName(agents, event.author.id, event.author.name)} ${action} ${subject}.`;
        }
        return `${action.charAt(0).toUpperCase() + action.slice(1)} ${subject}.`;
      };

      if (!isEqual(oldAssignment.team, newAssignment.team)) {
        messages.push(
          authorMessage("changed Team assignment", `from ${getOldTeamMessage()} to ${getNewTeamMessage()}`)
        );
      }

      const { newAgentName, oldAgentName } = getAgentMessage();

      if (!isEqual(oldAssignment.agent, newAssignment.agent)) {
        if (newAgentName) {
          messages.push(
            authorMessage(
              "changed Agent assignment",
              `from ${oldAgentName ?? "<strong>Unassigned</strong>"} to ${newAgentName}`
            )
          );
        } else if (oldAgentName) {
          messages.push(
            authorMessage("changed Agent assignment", `from ${oldAgentName} to <strong>Unassigned</strong>`)
          );
        }
      }

      return messages;
    }

    case EventType.SubjectChanged:
      return [
        // @ts-expect-error TS(2304): Cannot find name 'getAgentName'.
        `${getAgentName(agents, event.author.id, event.author.name)} changed ticket subject to <strong>${escapeString(
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          event.subject.new
        )}</strong>.`,
      ];

    case EventType.ChildTickets:
      return [
        // @ts-expect-error TS(2304): Cannot find name 'getAgentName'.
        `${getAgentName(agents, event.author.id, event.author.name)} merged <strong>${escapeString(
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          event.childTickets.diff.length.toString()
        )}</strong> following tickets into this ticket`,
      ];

    case EventType.RequesterChanged: {
      const changes = [];

      if (event.requester?.old.name !== event.requester?.new.name) {
        changes.push(
          `${getAgentName(
            agents,
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            event.author.id,
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            event.author.name
          )} changed requester’s name from <strong>${escapeString(
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            event.requester.old.name
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          )}</strong> to <strong>${escapeString(event.requester.new.name)}</strong>.`
        );
      }

      if (event.requester?.old.email !== event.requester?.new.email) {
        changes.push(
          `${getAgentName(
            agents,
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            event.author.id,
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            event.author.name
          )} changed requester’s email from <strong>${escapeString(
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            event.requester.old.email
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          )}</strong> to <strong>${escapeString(event.requester.new.email)}</strong>.`
        );
      }

      return changes;
    }

    case EventType.StatusChanged:
      if (!event.status) {
        return [];
      }

      if (event.author) {
        return [
          event.author.type === "rule"
            ? `Rule ${getRuleName(rulesMap, currentAgent, event.author.id)} changed ticket status from <strong>${
                TicketStatusFormattedValues[event.status.old]
              }</strong> to <strong>${TicketStatusFormattedValues[event.status.new]}</strong>.`
            : `${getAuthorName(
                agents,
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                event.author.id,
                event.author.name,
                event.author.email
              )} changed ticket status from <strong>${
                TicketStatusFormattedValues[event.status.old]
              }</strong> to <strong>${TicketStatusFormattedValues[event.status.new]}</strong>.`,
        ];
      }
      return [
        `Ticket status changed from <strong>${TicketStatusFormattedValues[event.status.old]}</strong> to <strong>${
          TicketStatusFormattedValues[event.status.new]
        }</strong>.`,
      ];

    case EventType.SiloChanged: {
      if (!event.silo) {
        return [];
      }
      if (event.author?.type === "rule") {
        return [
          `Rule ${getRuleName(rulesMap, currentAgent, event.author.id)} moved ticket from <strong>${
            TicketSiloFormattedValues[event.silo.old]
          }</strong> to <strong>${TicketSiloFormattedValues[event.silo.new]}</strong>.`,
        ];
      }
      if (event.author?.type === "agent") {
        return [
          // @ts-expect-error TS(2304): Cannot find name 'getAuthorName'.
          `${getAuthorName(agents, event.author.id, event.author.name, event.author.email)} moved ticket from <strong>${
            TicketSiloFormattedValues[event.silo.old]
          }</strong> to <strong>${TicketSiloFormattedValues[event.silo.new]}</strong>.`,
        ];
      }
      if (event.author?.type === "system") {
        return [
          `<b>HelpDesk</b> moved ticket from <strong>${TicketSiloFormattedValues[event.silo.old]}</strong> to <strong>${
            TicketSiloFormattedValues[event.silo.new]
          }</strong>.`,
        ];
      }
      if (event.author?.type === "client") {
        return [
          // @ts-expect-error TS(2304): Cannot find name 'getAuthorName'.
          `${getAuthorName(agents, event.author.id, event.author.name, event.author.email)} moved ticket from <strong>${
            TicketSiloFormattedValues[event.silo.old]
          }</strong> to <strong>${TicketSiloFormattedValues[event.silo.new]}</strong>.`,
        ];
      }
      return [
        `Ticket moved from <strong>${TicketSiloFormattedValues[event.silo.old]}</strong> to <strong>${
          TicketSiloFormattedValues[event.silo.new]
        }</strong>.`,
      ];
    }

    case EventType.Spam: {
      if (!event.spam) {
        return [];
      }
      const status = event.spam?.new.status === false ? "Not spam" : "Spam";

      const spamReason = event.spamReasonSource && event.spam.old.reason && getSpamReasonMessage(event.spam.old.reason);

      if (event.author) {
        if (event.author.type === Author.Rule) {
          return [
            `Rule ${getRuleName(
              rulesMap,
              currentAgent,
              event.author.id
            )} marked the ticket as <strong>${status}</strong>.`,
          ];
        }

        return [
          `${getAgentName(
            agents,
            event.author.id,
            event.author.name
          )} marked the ticket as <strong>${status}</strong>.`,
        ];
      }
      return [`Ticket marked as <strong>${status}</strong>${spamReason ? ` - ${spamReason}` : "."}`];
    }

    case EventType.CcChanged: {
      if (!event.cc) {
        return [];
      }
      const ccsCurrent = event.cc.new.map(cc => cc.email);
      const ccsPrevious = event.cc.old.map(cc => cc.email);
      const ccsAdded = difference(ccsCurrent, ccsPrevious).join(", ");
      const ccsRemoved = difference(ccsPrevious, ccsCurrent).join(", ");

      let authorName = null;

      if (event.author?.type === Author.Agent) {
        authorName = getAgentName(agents, event.author.id, event.author.name);
      }

      if (ccsAdded && ccsRemoved) {
        if (authorName) {
          return [
            `${authorName} added <strong>${escapeString(ccsAdded)}</strong> and removed <strong>${escapeString(
              ccsRemoved
            )}</strong> from CC.`,
          ];
        }
        return [
          `<strong>${escapeString(ccsAdded)}</strong> added and <strong>${escapeString(
            ccsRemoved
          )}</strong> removed from CC.`,
        ];
      }
      if (ccsAdded) {
        if (authorName) {
          return [`${authorName} added <strong>${escapeString(ccsAdded)}</strong> to CC.`];
        }
        return [`<strong>${escapeString(ccsAdded)}</strong> added to CC.`];
      }
      if (authorName) {
        return [`${authorName} removed <strong>${escapeString(ccsRemoved)}</strong> from CC.`];
      }
      return [`<strong>${escapeString(ccsRemoved)}</strong> removed from CC.`];
    }

    case EventType.FollowUpSent: {
      const events: TicketMessage[] = [
        event.author?.type === "rule"
          ? `Rule ${getRuleName(rulesMap, currentAgent, event.author.id)} sent follow-up to <strong>${escapeString(
              requesterName
            )}</strong>.`
          : `Auto follow-up sent to <strong>${escapeString(requesterName)}</strong>.`,
        <>
          {event.messageText
            ? parseEventMessageToHtml(event.messageText)
            : parseEventMessageToHtml(`Hello ${escapeString(
                requesterName
              )},\n\n We haven't heard back from you for some time. If you need
          any further help, please follow up on this email.\n\nThank you.`)}
        </>,
      ];

      return events;
    }

    case EventType.RatingRequestSent: {
      const events: TicketMessage[] = [
        event.author?.type === "rule"
          ? `Rule ${getRuleName(
              rulesMap,
              currentAgent,
              event.author.id
            )} sent feedback request to <strong>${escapeString(requesterName)}</strong>.`
          : `<strong>${escapeString(requesterName)}</strong> was asked to rate the customer service.`,
      ];

      if (event.ratingRequest?.text) {
        events.push(<>{parseEventMessageToHtml(event.ratingRequest.text)}</>);
      }
      return events;
    }

    case EventType.Rating:
      if (!event.rating) {
        return [];
      }
      return [<TicketEventRating rating={event.rating.new} key={event.id} />];

    case EventType.PriorityChanged: {
      let authorName = null;

      if (!event.priority) {
        return [];
      }
      if (event.author?.type === Author.Agent) {
        authorName = getAgentName(agents, event.author.id, event.author.name);
      }
      const events: TicketMessage[] = [
        event.author?.type === "rule"
          ? `Rule ${getRuleName(rulesMap, currentAgent, event.author.id)} changed ticket priority from <strong>${
              TICKET_PRIORITY_LABEL_MAP[event.priority.old] || "Unknown"
            }</strong> to <strong>${TICKET_PRIORITY_LABEL_MAP[event.priority.new] || "Unknown"}</strong>.`
          : `${authorName} changed ticket priority from <strong>${
              TICKET_PRIORITY_LABEL_MAP[event.priority.old] || "Unknown"
            }</strong> to <strong>${TICKET_PRIORITY_LABEL_MAP[event.priority.new] || "Unknown"}</strong>.`,
      ];

      return events;
    }

    case EventType.TagsChanged: {
      let events: string[] = [];

      if (!event.tags) {
        return [];
      }

      const isRuleEvent = event.author?.type === Author.Rule;

      const author = isRuleEvent
        ? `Rule ${getRuleName(rulesMap, currentAgent, event.author?.id)}`
        : getAgentName(agents, event.author?.id, event.author?.name);

      if (!settings.showAgentTagChangeEvents && !isRuleEvent) {
        return [];
      }

      let message = `${author}`;
      const addedTags = compact(difference(event.tags.new, event.tags.old).map(tagID => tagsMap[tagID]?.name));
      const removedTags = compact(difference(event.tags.old, event.tags.new).map(tagID => tagsMap[tagID]?.name));

      if (addedTags.length > 0) {
        message += ` added ${plural(addedTags.length, "tag", "tags")}: ${addedTags.join(", ")}`;
      }

      if (removedTags.length > 0) {
        if (addedTags.length > 0) {
          message += " and";
        }

        message += ` removed ${plural(removedTags.length, "tag", "tags")}: ${removedTags.join(", ")}`;
      }

      if (addedTags.length > 0 || removedTags.length > 0) {
        events = [`${message}.`];
      }

      return events;
    }

    default:
      return [];
  }
}
